import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import classes from './CustomBiWeeklyPicker.module.scss';
import colors from '../../../scss/colors';
import { generalEnums } from '../../../utils/enums';
import { ArrowForward } from '@mui/icons-material';

dayjs.extend(isBetween);

export default function CustomBiWeeklyPicker({
  userDate,
  setUserDate,
  disabledBeforeIncluded,
  daysAvailability = [],
  dateDisablingAbility = false,
  backgroundColor = colors.WHITE,
  sx,
  loading = false,
  showControls = true,
  dateBackAction = () => { },
  dateForwardAction = () => { }
}) {
  const [dateToShow, setDateToShow] = useState(dayjs(userDate || undefined));
  const [selectedRange, setSelectedRange] = useState(null);
  const currentDay = useMemo(() => dayjs(), []);

  const updateSelection = (startDate) => {
    const start = dayjs(startDate).startOf('day');
    const end = start.add(13, 'day').endOf('day');
    setSelectedRange({
      start: start.toDate(),
      end: end.toDate(),
    });
    setUserDate(start.toDate());
  };

  const handleDateClick = (day) => {
    if (isDayDisabled(day) || isDisabled(day)) return;
    const choosedDate = dayjs(userDate)
    const clickedDate = dayjs(day);
    const daysSinceAnchor = clickedDate.diff(choosedDate, 'day');

    const periodsPassed = Math.floor(daysSinceAnchor / 13);

    const snappedStartDate = choosedDate.add(periodsPassed * 14, 'day');

    updateSelection(snappedStartDate);
  };

  const handleNextPeriod = () => {
    const nextStart = dayjs(selectedRange?.start || userDate).add(14, 'day');
    dateForwardAction(nextStart.format(generalEnums.DATEFORMAT));
    updateSelection(nextStart);
    setDateToShow(nextStart);
  };

  const handlePreviousPeriod = () => {
    const prevStart = dayjs(selectedRange?.start || userDate).subtract(14, 'day');
    dateBackAction(prevStart.format(generalEnums.DATEFORMAT));
    updateSelection(prevStart);
    setDateToShow(prevStart);
  };

  const handleNextMonth = () => setDateToShow(prev => prev.add(1, 'month'));
  const handlePrevMonth = () => setDateToShow(prev => prev.subtract(1, 'month'));

  const firstDayOfTheMonth = useMemo(() => dateToShow.clone().startOf('month'), [dateToShow]);

  const firstDayOfGrid = useMemo(() => {
    return firstDayOfTheMonth.day() === 0
      ? firstDayOfTheMonth.subtract(1, 'day').startOf('week')
      : firstDayOfTheMonth.startOf('week');
  }, [firstDayOfTheMonth]);

  const calendarGrid = useMemo(() => {
    const weeks = [];
    let cursor = firstDayOfGrid.clone();
    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        week.push(cursor.toDate());
        cursor = cursor.add(1, 'day');
      }
      weeks.push(week);
    }
    return weeks;
  }, [firstDayOfGrid]);

  const getClassForVariant = (day) => {
    const dayObj = dayjs(day);
    const isDifferentMonth = dayObj.month() !== dateToShow.month();
    let classList = isDifferentMonth ? classes.nextMonth : classes.default;
    if (selectedRange) {
      const start = dayjs(selectedRange.start);
      const end = dayjs(selectedRange.end);
      // Check if day is within the 14-day range
      if (dayObj.isBetween(start, end, 'day', '[]')) {
        classList += ` ${classes.selected}`;
        if (dayObj.isSame(start, 'day')) classList += ` ${classes.firstSelection}`;
        if (dayObj.isSame(end, 'day')) classList += ` ${classes.lastSelection}`;
      }
    }
    return classList;
  };

  const isDisabled = (day) => (disabledBeforeIncluded ? dayjs(day).isBefore(disabledBeforeIncluded, 'day') : false);
  const isDayDisabled = (day) =>
    dateDisablingAbility && !daysAvailability.includes(dayjs(day).format(generalEnums.DATEFORMAT));

  useEffect(() => {
    if (userDate) {
      updateSelection(userDate);
    } else {
      updateSelection(currentDay);
    }
  }, []);

  return (
    <div className={classes.mainWrapper} style={{ backgroundColor, ...sx }}>
      <div className={classes.calendarHeaderWrapper}>
        <div className={classes.actionButtons}>
          <ArrowBackIosNewIcon fontSize="small" onClick={handlePrevMonth} className={classes.pointer} />
          <p>{dateToShow.format('MMMM, YYYY')}</p>
          <ArrowForwardIosIcon fontSize="small" onClick={handleNextMonth} className={classes.pointer} />
        </div>
      </div>
      <div className={classes.weekDaysWrapper}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className={`md:text-base !text-sm ${classes.weekDayCell}`}>{d}</div>
        ))}
      </div>
      {calendarGrid.map((week, i) => (
        <div key={`week-${i}`} className={classes.calendarBiMonthlyWrapper}>
          {week.map((day) => (
            <button
              key={day.toString()}
              onClick={() => handleDateClick(day)}
              className={`${classes.calendarDayCell} ${getClassForVariant(day)} ${(isDisabled(day) || isDayDisabled(day)) ? classes.disabled : ''
                } ${loading ? classes.loadingText : ''}`}
            >
              {day.getDate()}
            </button>
          ))}
        </div>
      ))}
      <div className='flex justify-center items-center w-full my-4 border-t border-gray-300 pt-2'>
        <div className='text-navy-blue'>
          {/* <span>Period: </span>s */}
          <div className='flex items-center gap-2 font-medium text-lg pt-2'>
            {selectedRange
              ? `${dayjs(selectedRange.start).format('MMM D')} - ${dayjs(selectedRange.end).format('MMM D')}`
              : '...'}
          </div>
        </div>
      </div>
      {
        showControls === true && (
          <div className="flex justify-between items-center mt-4 mx-2">
            <button
              className='flex text-secondary items-center gap-2 text-gray-600 hover:text-gray-800 cursor-pointer md:text-center text-left'
              onClick={handlePreviousPeriod}
            >
              <ArrowBackIosNewIcon fontSize="small" />
              Previous Period
            </button>
            <button className='flex text-secondary items-center gap-2 text-gray-600 hover:text-gray-800 cursor-pointer md:text-center text-right' onClick={handleNextPeriod}>
              Next Period
              <ArrowForwardIosIcon fontSize="small" />
            </button>
          </div>
        )
      }
    </div>
  );
}